import React from 'react';
import {
    ClosingCountdown,
    FeeAgreement,
    FeeLineItem,
    VetValueFeeLedger,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { VETVALUE_FEES_PENDING_REVIEW_TEXT } from '../../../utils/flowOfFundsFormat';
import { LevelOfService, isSfas } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { SfasFeeParams } from '../../../slices/marketplace/loi-comparison/consideration-fees';
import {
    VvConnectFeeRowDto,
    VvConnectFeesDto,
} from '../../../slices/marketplace/loi-comparison/models/vv-connect-fees.model';
import {
    VvConnectFeesLabelsColumn,
    VvConnectFeesSection,
} from '../../../containers/ConnectMarketplaceDashboard/LoiComparisonTab/LoiComparisonSummary/VvConnectFeesSection';
import { FeeAgreementBlock } from '../FeeAgreementBlock/FeeAgreementBlock';
import { ClosingTickler } from '../ClosingTickler/ClosingTickler';
import '../../../containers/ConnectMarketplaceDashboard/LoiComparisonTab/LoiComparisonSummary/LoiComparisonSummary.scss';
import './VetValueFees.scss';

const EMPTY_ROW: VvConnectFeeRowDto = { fee: null, credits: null };

const toFeeRow = (item: FeeLineItem): VvConnectFeeRowDto => ({ fee: item.fee, credits: item.credit });

const sumProcessFees = (items: FeeLineItem[]): VvConnectFeeRowDto =>
    items.reduce<VvConnectFeeRowDto>(
        (total, item) => ({
            fee: (total.fee ?? 0) + (item.fee ?? 0),
            credits: (total.credits ?? 0) + (item.credit ?? 0),
        }),
        { fee: null, credits: null }
    );

const toVvConnectFeesDto = (fees: VetValueFeeLedger): VvConnectFeesDto => ({
    transactionFeePercent: { fee: fees.transactionFeePercent, credits: null },
    transactionFairValue: { fee: fees.projectedTransactionValue, credits: null },
    transactionFeeDollar: { fee: fees.projectedTransactionFee, credits: null },
    applicationFee: toFeeRow(fees.applicationFee),
    monthlyRetainerFees: toFeeRow(fees.monthlyRetainerFees),
    processFees: sumProcessFees(fees.processFees),
    buyerIdNdaFees: EMPTY_ROW,
    pipPiqProvisionFees: EMPTY_ROW,
    dfmProvisionFees: EMPTY_ROW,
    lateDelayFees: toFeeRow(fees.lateDelayFees),
    engagementSuspensionFees: toFeeRow(fees.engagementSuspensionFees),
    totalNetFee: EMPTY_ROW,
    percentOfFairValue: EMPTY_ROW,
    netFeePaymentAtClosing: EMPTY_ROW,
});

const buildAgreementDescription = (agreement: FeeAgreement): string => {
    const labels = agreement.parties.map((party) => party.label);
    return labels.length === 0
        ? 'These fees must be agreed before they are finalized.'
        : `${labels.join(' and ')} must agree to these fees before they are finalized.`;
};

interface VetValueFeesProps {
    fees: VetValueFeeLedger;
    levelOfService: LevelOfService;
    onAgree?: () => void;
    isSaving?: boolean;
    closing?: ClosingCountdown | null;
    sfasParams?: SfasFeeParams | null;
    isPendingVetValueReview?: boolean;
}

export const VetValueFees = ({
    fees,
    levelOfService,
    onAgree,
    isSaving,
    closing,
    sfasParams,
    isPendingVetValueReview = false,
}: VetValueFeesProps) => {
    return (
        <div className="ff-vvfees">
            <div className="ff-vvfees__tickler-slot">
                <ClosingTickler closing={closing} />
            </div>

            {isPendingVetValueReview ? (
                <div className="ff-vvfees__pending text-muted">{VETVALUE_FEES_PENDING_REVIEW_TEXT}</div>
            ) : (
                <div className="loi-comparison-summary">
                    <div className="summary-inner vv-fees-section">
                        <VvConnectFeesLabelsColumn isSfasEngagement={isSfas(levelOfService)} />
                        <VvConnectFeesSection
                            fees={toVvConnectFeesDto(fees)}
                            portionPaidAtClose={fees.portionPaidAtClose}
                            projectedTransactionValue={fees.projectedTransactionValue}
                            projectedTransactionFee={fees.projectedTransactionFee}
                            levelOfService={levelOfService}
                            sfasParams={sfasParams}
                            totalCreditsOverride={fees.totalCredits}
                            netProjectedTotalFeesOverride={fees.netProjectedTotalFees}
                        />
                    </div>
                </div>
            )}

            <FeeAgreementBlock
                agreement={fees.agreement}
                description={buildAgreementDescription(fees.agreement)}
                onAgree={onAgree}
                isSaving={isSaving}
            />
        </div>
    );
};
